<template>
    <div class="h-100 w-100 panorama-out-container" @drop="onDrop" @dragover="onDragOver">
        <div
            :id="id"
            class="panorama-container"
            @mousedown="getViewerCoordinates"
            @mouseup="removeListenerViewerCoordinates"
            @mouseleave="mouseLeftViewer"
            v-touch:start="getViewerCoordinatesTouch"
            v-touch:end="removeListenerViewerCoordinatesTouch"
        ></div>

        <transition name="slide">
            <div v-if="newCoords.isListening" class="selecting-coords-container">
                <button @click="stopListeningToCoords">
                    <icon name="times" scale="0.8" color="var(--light)" />
                </button>
                <p class="text-10 text-weight-bold">
                    {{ newCoords.title ? newCoords.title : 'Clique no local da imagem' }}
                </p>
            </div>
        </transition>

        <div v-if="isLoadingImage" class="h-100 w-100 loading-container">
            <b-spinner :small="isMobile" variant="secondary" />
        </div>
    </div>
</template>
<script>
// ==================
// Vuex
// ==================
import { mapState } from 'vuex'

// ==================
// Pannellum settings
// ==================
// para debugar o pannellum e ter mensagens de erro mais especificas, descomente as duas linhas abaixo
// import 'pannellum/src/js/pannellum.js'
// import 'pannellum/src/js/libpannellum.js'
// pannellum padrão
import 'pannellum/build/pannellum.js'
import 'pannellum/build/pannellum.css'

// ==================
// Custom Icons
// ==================
const ISSUE_ICON = require('@/assets/img/misc/issue-icon.svg')
const ARROW_CONNECTION_ICON = require('@/assets/img/misc/arrow-connection-icon.svg')

const TRASH_ICON = require('@/assets/img/misc/trash-white.svg')
const MOVE_ICON = require('@/assets/img/misc/move-white.svg')

/**
 * Summary.
 * Use this component to show a panoramic equirrec./cubemap image.
 * The external component used to render images is Pannellum.
 * The documentantion guide can be found here:
 * https://pannellum.org/documentation/api/
 *
 * @param {Boolean} imageComparisonEnabled
 * @param {Boolean} isMobile
 * @param {Boolean} tour
 * @param {Boolean} configTour
 *
 * @param {String} side - left/right
 * @param {String} id
 *
 * @param {Number} lastViewpointId
 *
 * @param {Object} coordinates
 * @param {Object} picture - {id, url, date, pitch, yaw, horizonPitch, horizonRow, issues: Array, connections: Array}
 *
 * @event onNewCoordsSelected
 * @event onPanoramaInstanceChange
 * @event onCurrentCoordinatesChange
 * @event onStartDraggingPanorama
 * @event onStopDraggingPanorama
 * @event onHotspotClicked - ({type,id})
 * @event onRequestDeleteHotspot - ({type,id})
 * @event onImageLoad
 * @event drop
 * @event dragover
 */

export default {
    name: 'PanoramaViewer',
    props: {
        imageComparisonEnabled: Boolean,
        isMobile: Boolean,
        tour: {
            type: Boolean,
            default: false,
        },
        scenes: {
            type: Object,
            default: () => {
                return {}
            },
        },
        firstScene: {
            type: String,
            default: '',
        },

        side: String,
        id: {
            type: String,
            required: true,
        },

        lastViewpointId: Number,

        coordinates: Object,
        picture: Object,
    },

    computed: {
        ...mapState(['sidebar']),

        hasPermissionToChange() {
            return true
        },
    },

    watch: {
        imageComparisonEnabled: function () {
            this.panoramaInstance.resize()
        },

        picture: function (newValue, oldValue) {
            if (!this.isLoadingImage) {
                // avoid unneccessary reloads
                if (this.picture.url) {
                    if (newValue.url != oldValue.url) {
                        this.setupPanorama()
                    } else {
                        this.reloadHotspots()
                    }
                } else if (this.picture.left) {
                    this.setupPanorama(false)
                }
            }
        },

        scenes() {
            setTimeout(() => {
                this.setupPanorama()
            }, 500)
        },

        coordinates: {
            handler(newValue, oldValue) {
                this.panoramaInstance.setPitch(this.coordinates.pitch, false)
                this.panoramaInstance.setYaw(this.coordinates.yaw, false)
            },
            deep: true,
        },
    },

    data() {
        return {
            interval: null,
            componentId: null,

            // Viewer configuration
            panoramaInstance: null,
            maxSpeed: 200,
            speed: null,
            isLoadingImage: true,

            //Hotspots
            isHotspotContextMenuOpen: false,
            selectedHotspotId: null,
            selectedHotspotExtension: null,
            selectedHotspotTargetName: null,
            issuesHotspots: [],
            connectionsHotspots: [],
            tourConnectionsHotspots: [],

            // Coords listenner
            newCoords: {
                title: null,
                isListening: false,
                hotspotId: -1,
                isEditing: false,
                pitch: 0,
                yaw: 0,
            },
        }
    },
    methods: {
        // =========================
        // Utils
        // =========================
        startControl(func) {
            if (!this.interval) {
                this.interval = setInterval(this[func], 1)
            }
        },
        stopControl() {
            clearInterval(this.interval)
            this.interval = false
        },
        lookAtLeft() {
            let pitch = this.panoramaInstance.getPitch()
            let yaw = this.panoramaInstance.getYaw() - 0.5
            let hfov = this.panoramaInstance.getHfov()
            this.panoramaInstance.lookAt(pitch, yaw, hfov, false)
        },

        lookAtRight() {
            let pitch = this.panoramaInstance.getPitch()
            let yaw = this.panoramaInstance.getYaw() + 0.5
            let hfov = this.panoramaInstance.getHfov()
            this.panoramaInstance.lookAt(pitch, yaw, hfov, false)
        },

        lookAtUp() {
            let pitch = this.panoramaInstance.getPitch() + 0.5
            let yaw = this.panoramaInstance.getYaw()
            let hfov = this.panoramaInstance.getHfov()
            this.panoramaInstance.lookAt(pitch, yaw, hfov, false)
        },

        lookAtDown() {
            let pitch = this.panoramaInstance.getPitch() - 0.5
            let yaw = this.panoramaInstance.getYaw()
            let hfov = this.panoramaInstance.getHfov()
            this.panoramaInstance.lookAt(pitch, yaw, hfov, false)
        },

        lookBack() {
            let pitch = this.panoramaInstance.getPitch()
            let yaw = this.panoramaInstance.getYawBounds()[0] + this.panoramaInstance.getYaw()
            this.panoramaInstance.lookAt(pitch, yaw)
        },

        zoomIn() {
            this.panoramaInstance.setHfov(this.panoramaInstance.getHfov() - 30)
        },

        zoomOut() {
            this.panoramaInstance.setHfov(this.panoramaInstance.getHfov() + 30)
        },

        /**
         * Summary.
         * Use this method to enable panorama to start capturing coordinates.
         *
         * @param {String} title - The title to explain to user what to do
         */
        startListeningToCoords(title, id = -1, isEditing = false) {
            this.newCoords.isListening = true
            this.newCoords.isEditing = isEditing
            this.newCoords.hotspotId = id
            this.newCoords.title = title
        },

        stopListeningToCoords() {
            let hotspot = document.getElementById('hotspot-' + this.newCoords.hotspotId)
            if (hotspot) {
                hotspot.style.opacity = 1
                hotspot.style.pointerEvents = 'initial'
            }
            this.newCoords.isListening = false
            this.newCoords.isEditing = false
            this.newCoords.title = null
            this.newCoords.pitch = 0
            this.newCoords.yaw = 0
        },

        /**
         * Summary.
         * Get a panorama thumbnail based on coordinates
         *
         * @param pitch
         * @param yaw
         *
         * @returns {Promise(base64)}
         */
        generateThumbnailFromCurrentCoords() {
            return new Promise((resolve) => {
                let screenshot = this.panoramaInstance
                    .getRenderer()
                    .render(
                        (this.panoramaInstance.getPitch() * Math.PI) / 180,
                        (this.panoramaInstance.getYaw() * Math.PI) / 180,
                        (this.panoramaInstance.getHfov() * Math.PI) / 180,
                        { returnImage: true }
                    )

                let tempCanvas = document.createElement('canvas')
                let tempContext = tempCanvas.getContext('2d')

                tempCanvas.width = 800
                tempCanvas.height = 600

                let image = new Image()
                image.onload = () => {
                    tempContext.drawImage(image, (image.width - 800) / 2, (image.height - 600) / 2, 800, 600, 0, 0, 800, 600)
                    let issueThumbnail = tempCanvas.toDataURL()
                    tempCanvas.remove()
                    resolve(issueThumbnail)
                }
                image.src = screenshot
            })
        },

        /**
         * Summary.
         * Triggered when users start's moving viewer
         *
         * @param {Object} mouseEvent
         */
        getViewerCoordinates(mouseEvent) {
            this.$emit('onStartDraggingPanorama', mouseEvent)

            if (mouseEvent.button == 0) {
                let annotationElement = document.getElementById('hotspot-' + this.selectedHotspotId)
                let coords = []

                // Selecting coords
                if (this.newCoords.isListening) {
                    // NEW COORDS
                    if (!this.newCoords.isEditing) {
                        let grabber = document.getElementsByClassName('pnlm-grab')[0]
                        let html = document.getElementsByTagName('html')[0]
                        coords = this.panoramaInstance.mouseEventToCoords(mouseEvent)
                        html.style.cursor = 'wait'
                        html.style.pointerEvents = 'none'

                        if (coords[0] && coords[1]) {
                            this.$emit('onNewCoordsSelected', {
                                coords: { pitch: coords[0], yaw: coords[1] },
                                image_id: this.picture.id,
                            })

                            this.stopListeningToCoords()

                            html.style.cursor = 'auto'
                            html.style.pointerEvents = 'all'
                        }
                    }
                    // EDITING COORDS
                    else {
                        let grabber = document.getElementsByClassName('pnlm-grab')[0]
                        let html = document.getElementsByTagName('html')[0]
                        coords = this.panoramaInstance.mouseEventToCoords(mouseEvent)
                        html.style.cursor = 'wait'
                        html.style.pointerEvents = 'none'

                        if (coords[0] && coords[1]) {
                            this.$emit('onEditedCoordsSelected', {
                                coords: { pitch: coords[0], yaw: coords[1] },
                                image_id: this.picture.id,
                                hotspot_id: this.newCoords.hotspotId,
                            })

                            this.stopListeningToCoords()

                            html.style.cursor = 'auto'
                            html.style.pointerEvents = 'all'
                        }
                    }
                }

                // Clicked on the delete option of a HS
                if (mouseEvent.target.classList.contains('option') || mouseEvent.target.classList.contains('option-icon')) {
                    mouseEvent.preventDefault()
                    // Connection
                    if (mouseEvent.target.classList.contains('connection')) {
                        if (mouseEvent.target.classList.contains('delete')) {
                            this.$emit('onRequestDeleteHotspot', { type: 'connection', id: Number(this.selectedHotspotId.split('-')[1]) })
                        }
                    } else {
                        // Issue
                        if (mouseEvent.target.classList.contains('delete')) {
                            this.$emit('onRequestDeleteHotspot', { type: 'issue', id: this.selectedHotspotId })
                        }

                        if (mouseEvent.target.classList.contains('move')) {
                            this.$emit('onRequestMoveHotspot', { type: 'issue', id: this.selectedHotspotId })
                        }
                    }
                }
            }

            this.closeHotspotContextMenu()

            let element = document.getElementById(this.id)
            if (mouseEvent.button == 0) {
                element.addEventListener('mousemove', this.onCurrentCoordinatesChange)
            }
        },

        /**
         * Summary.
         * Triggered when users touches the viewer
         *
         * @param {Object} mouseEvent
         */
        getViewerCoordinatesTouch(mouseEvent) {
            let element = document.getElementById(this.id)
            element.addEventListener('touchmove', this.onCurrentCoordinatesChange, { passive: true })
        },

        convertEventToCoords(mouseEvent) {
            let coords = this.panoramaInstance.mouseEventToCoords(mouseEvent)

            return {
                pitch: coords[0],
                yaw: coords[1],
            }
        },

        // =======================
        // Hotspots
        // =======================
        convertIssuesToHotspots() {
            this.issuesHotspots = []
            if (this.picture.issues) {
                this.picture.issues.forEach((issue) => {
                    this.issuesHotspots.push(this.issueToHotspot(issue))
                })
            }
        },

        convertConnectionsToHotspots() {
            this.connectionsHotspots = []
            if (this.picture.connections) {
                this.picture.connections.forEach((connection) => {
                    if (!this.connectionsHotspots.some((c) => Number(c.id.split('-')[1]) == connection.id))
                        this.connectionsHotspots.push(this.connectionToHotspot(connection))
                })
            }
        },

        convertTourConnectionsToHotspots() {
            this.tourConnectionsHotspots = []
            if (this.picture.tourConnections) {
                this.picture.tourConnections.forEach((connection) => {
                    if (
                        connection.pitch != null &&
                        connection.yaw != null &&
                        !this.tourConnectionsHotspots.some((c) => Number(c.id.split('-')[1]) == connection.viewpoint_target)
                    ) {
                        this.tourConnectionsHotspots.push(this.tourConnectionToHotspot(connection))
                    }
                })
            }
        },

        issueToHotspot(issue) {
            let isClosedCss = ''
            switch (issue.status) {
                case 'open':
                    break
                case 'closed':
                    isClosedCss = 'ci-hotspot-closed'
                    break
                case 'in_review':
                    break
            }
            return {
                id: issue.id,
                cssClass: issue.cssClass
                    ? issue.cssClass
                    : 'ci-hotspot-base pnlm-hotspot-base pnlm-hotspot pnlm-sprite pnlm-info pnlm-tooltip pnlm-pointer ci-hotspot-issue ' +
                      isClosedCss +
                      (' ci-hotspot-' + issue.priority),
                pitch: issue.pitch,
                yaw: issue.yaw,
                clickHandlerArgs: { id: issue.id },
                clickHandlerFunc: (event, { id }) => {
                    this.onHotspotClicked('issue', issue.id)
                },
                createTooltipArgs: {
                    id: issue.id,
                    extension: 'issue',
                    status: issue.status,
                },
                createTooltipFunc: this.createHotspot,
            }
        },

        connectionToHotspot(connection) {
            return {
                id: 'con-' + connection.id,
                cssClass: 'ci-hotspot-base pnlm-hotspot-base pnlm-hotspot pnlm-sprite pnlm-info pnlm-tooltip pnlm-pointer ci-hotspot-connection',
                pitch: connection.pitch,
                yaw: connection.yaw,
                clickHandlerArgs: { target: connection.viewpoint_target },
                clickHandlerFunc: (event, { target }) => {
                    this.onHotspotClicked('connection', connection.id)
                },
                createTooltipArgs: {
                    id: 'con-' + connection.id,
                    extension: 'connection',
                    status: null,
                    target_name: connection.target_name,
                },
                createTooltipFunc: this.createHotspot,
            }
        },

        tourConnectionToHotspot(connection) {
            return {
                id: 'tcon-' + connection.viewpoint_target.id,
                cssClass:
                    'ci-hotspot-base pnlm-hotspot-base pnlm-hotspot pnlm-sprite pnlm-info pnlm-tooltip pnlm-pointer ci-hotspot-connection creating-tour-hs',
                pitch: connection.pitch,
                yaw: connection.yaw,
                clickHandlerArgs: { target: connection.viewpoint_target.id },
                clickHandlerFunc: (event, { target }) => {
                    this.onHotspotClicked('tourConnection', connection.viewpoint_target)
                },
                createTooltipArgs: {
                    id: 'tcon-' + connection.viewpoint_target.id,
                    extension: 'tour-connection',
                    status: null,
                    target_name: connection.viewpoint_target.name,
                },
                createTooltipFunc: this.createHotspot,
            }
        },

        openHotspotContextMenu(id, extension, open, target_name) {
            if (!this.isHotspotContextMenuOpen && this.hasPermissionToChange && extension != 'tour-connection') {
                this.isHotspotContextMenuOpen = true
                this.selectedHotspotId = id
                this.selectedHotspotExtension = extension
                this.selectedHotspotTargetName = target_name

                let img = ISSUE_ICON

                if (extension == 'connection') {
                    img = ARROW_CONNECTION_ICON
                }

                let el = document.getElementById('hotspot-' + id)
                if (extension == 'issue') {
                    el.innerHTML = `
                    <div class="menu-options">
                        <div class="move-option option move ${extension}">
                        <img class="option-icon move ${extension}" src="${MOVE_ICON}"/>
                        </div>
                        <div class="delete-option option delete ${extension}">
                        <img class="option-icon delete ${extension}" src="${TRASH_ICON}"/>
                        </div>
                    </div>
                    <div class="inner-div">
                        <div class="center-div">
                        <img class="hotspot-icon" src="${img}"/>
                    `

                    el.classList.add('context-menu-active')
                }

                if (extension == 'connection') {
                    el.classList.add('custom-tooltip')
                    var span = document.createElement('span')
                    span.innerHTML = target_name
                    el.appendChild(span)
                    span.style.width = span.scrollWidth - 20 + 'px'
                    span.style.marginLeft = -(span.scrollWidth - el.offsetWidth) / 2 + 'px'
                    span.style.marginTop = -span.scrollHeight - 12 + 'px'
                }
            }
        },

        closeHotspotContextMenu(target_name) {
            if (this.isHotspotContextMenuOpen) {
                let el = document.getElementById('hotspot-' + this.selectedHotspotId)
                let img = ISSUE_ICON

                if (this.selectedHotspotExtension == 'connection') {
                    img = ARROW_CONNECTION_ICON
                }

                el.innerHTML = '<div class="inner-div"><div class="center-div"><img class="hotspot-icon" src="' + img + '"/><div/></div>'
                if (el != null) el.classList.remove('context-menu-active')
                if (this.selectedHotspotExtension == 'connection') {
                    el.classList.add('custom-tooltip')
                    var span = document.createElement('span')
                    span.innerHTML = this.selectedHotspotTargetName
                    el.appendChild(span)
                    span.style.width = span.scrollWidth - 20 + 'px'
                    span.style.marginLeft = -(span.scrollWidth - el.offsetWidth) / 2 + 'px'
                    span.style.marginTop = -span.scrollHeight - 12 + 'px'
                }
                this.selectedHotspotTargetName = null
                this.selectedHotspotId = null
                this.isHotspotContextMenuOpen = false
                this.isDeleteModalOpen = false
            }
        },

        createHotspot(hotspotDiv, { id, extension, status, target_name }) {
            let img = ISSUE_ICON

            if (extension != 'issue') {
                img = ARROW_CONNECTION_ICON
            }

            if (extension == 'tour-connection') {
                hotspotDiv.id = 'hotspot-' + id
                hotspotDiv.innerHTML =
                    '<div class="menu-options"></div><div class="inner-div" style="background-image: url(' +
                    img +
                    ');background-size: 55%; background-repeat: no-repeat; background-position: center;">'

                hotspotDiv.draggable = true
                hotspotDiv.ondragstart = (ev) => {
                    ev.dataTransfer.setData('text', JSON.stringify({ viewpoint_target: { id: id.split('-')[1] } }))
                }
                hotspotDiv.ondragover = (ev) => {
                    ev.preventDefault()
                }
            } else {
                hotspotDiv.id = 'hotspot-' + id
                hotspotDiv.classList.add('d-flex', 'justify-content-center', 'align-items-center')
                hotspotDiv.innerHTML =
                    '<div class="menu-options"></div><div class="inner-div"><div class="center-div"><img class="hotspot-icon" src="' + img + '"/>'
            }

            if (extension != 'issue') {
                hotspotDiv.classList.add('custom-tooltip')
                var span = document.createElement('span')
                span.innerHTML = target_name
                hotspotDiv.appendChild(span)
                span.style.width = span.scrollWidth - 20 + 'px'
                span.style.marginLeft = -(span.scrollWidth - hotspotDiv.offsetWidth) / 2 + 'px'
                span.style.marginTop = -span.scrollHeight - 12 + 'px'
            }

            // Event to open the delete option
            hotspotDiv.addEventListener('contextmenu', (e) => {
                e.preventDefault()
                this.openHotspotContextMenu(id, extension, status, target_name)
            })
            if (extension == 'tour-connection') {
                hotspotDiv.addEventListener('mouseenter', (e) => {
                    e.preventDefault()
                    this.$emit('onConnectionHover', id.split('-')[1])
                })
                hotspotDiv.getElementsByClassName('inner-div')[0].addEventListener('mouseenter', (e) => {
                    e.preventDefault()
                    this.$emit('onConnectionHover', id.split('-')[1])
                })
                hotspotDiv.addEventListener('mouseout', (e) => {
                    e.preventDefault()
                    this.$emit('onConnectionHover', -1)
                })
            }
        },

        /**
         * Summary.
         * Remove all current hotspots from current scene and re-add new ones.
         *
         */
        reloadHotspots() {
            if (this.panoramaInstance) {
                this.clearHotspots()

                this.convertIssuesToHotspots()
                this.convertConnectionsToHotspots()
                this.convertTourConnectionsToHotspots()

                let hps = this.issuesHotspots.concat(this.connectionsHotspots).concat(this.tourConnectionsHotspots)

                if (hps.length) {
                    hps.forEach((h) => {
                        this.panoramaInstance.addHotSpot(h)
                    })
                }
            }
        },

        /**
         * Summary.
         * Remove all Hotspots form current scene.
         *
         */
        clearHotspots() {
            if (this.panoramaInstance) {
                let hps = this.issuesHotspots.concat(this.connectionsHotspots).concat(this.tourConnectionsHotspots)

                if (hps.length) {
                    try {
                        hps.forEach((h) => {
                            this.panoramaInstance.removeHotSpot(h.id)
                        })
                    } catch {
                        console.warn('PanoramaViewer: Try to remove unexistent HotsPots')
                    } finally {
                        this.issuesHotspots = []
                        this.connectionsHotspots = []
                        this.tourConnectionsHotspots = []
                    }
                }
            }
        },

        // =========================
        // Initialization methods
        // =========================
        init() {
            this.panoramaInstance = pannellum.viewer(this.id, {
                ...this.config,
                autoLoad: true,
                showControls: false,
                disableKeyboardCtrl: false,
                hfov: 120,
                friction: 1.0,
                compass: false,
                hotSpots: this.issuesHotspots.concat(this.connectionsHotspots),
            })
            this.onPanoramaInstanceChange()
        },

        customInit(config) {
            this.convertConnectionsToHotspots()
            this.convertIssuesToHotspots()
            this.convertTourConnectionsToHotspots()

            if (this.panoramaInstance) {
                this.destroy()
            }

            const isMobile = this.$Mobile.verify()
            this.panoramaInstance = pannellum.viewer(this.id, {
                ...config,
                autoLoad: true,
                showControls: false,
                disableKeyboardCtrl: false,
                hfov: isMobile ? 60 : 120,
                maxHfov: isMobile ? 60 : 120,
                minHfov: isMobile ? 20 : 50,
                friction: 1.0,
                compass: false,
                hotSpotDebug: false,
                hotSpots: this.issuesHotspots.concat(this.connectionsHotspots).concat(this.tourConnectionsHotspots),
            })

            this.panoramaInstance.on('load', () => {
                this.isLoadingImage = false
                this.$emit('onImageLoad')
            })

            this.onPanoramaInstanceChange()
        },

        customTourInit() {
            const isMobile = this.$Mobile.verify()
            this.panoramaInstance = pannellum.viewer(this.id, {
                autoLoad: true,
                showControls: false,
                disableKeyboardCtrl: false,
                hfov: isMobile ? 60 : 120,
                maxHfov: isMobile ? 60 : 120,
                minHfov: isMobile ? 20 : 50,
                friction: 1.0,
                compass: false,
                hotSpotDebug: false,
                default: {
                    firstScene: this.firstScene,
                    sceneFadeDuration: 500,
                },
                scenes: this.scenes,
            })

            this.panoramaInstance.on('load', () => {
                this.isLoadingImage = false
                this.$emit('onImageLoad')
            })

            this.onPanoramaInstanceChange()
        },

        setupPanorama(verifyConnections = true) {
            this.isLoadingImage = true
            if (!this.tour) {
                let config
                if (this.picture.top) {
                    config = {
                        type: 'cubemap',
                        cubeMap: [this.picture.front, this.picture.right, this.picture.back, this.picture.left, this.picture.top, this.picture.bottom],
                    }
                } else {
                    config = {
                        type: 'equirectangular',
                        panorama: this.picture.url,
                    }
                }
                if (this.picture.yaw) config.yaw = this.picture.yaw
                if (this.picture.horizonPitch) config.horizonPitch = this.picture.horizonPitch
                if (this.picture.horizonRoll) config.horizonRoll = this.picture.horizonRoll
                this.customInit(config)
            } else if (Object.keys(this.scenes).length) {
                this.customTourInit()
            }

            if (this.picture.connections && this.picture.connections.length && this.lastViewpointId && verifyConnections) {
                let goBackConnection = this.picture.connections.find((con) => con.viewpoint_target == this.lastViewpointId)
                if (goBackConnection) {
                    this.panoramaInstance.lookAt(
                        this.panoramaInstance.getPitch(),
                        this.panoramaInstance.getYawBounds()[0] + goBackConnection.yaw,
                        this.panoramaInstance.getHfov(),
                        false
                    )
                }
            }
        },

        destroy() {
            this.panoramaInstance.destroy()
        },

        // =========================
        // Events
        // =========================
        onCurrentCoordinatesChange() {
            this.$emit('onCurrentCoordinatesChange')
        },

        onPanoramaInstanceChange() {
            this.$emit('onPanoramaInstanceChange', this.panoramaInstance)
        },

        onHotspotClicked(type, id) {
            this.$emit('onHotspotClicked', { type, id })
        },

        removeListenerViewerCoordinates(mouseEvent) {
            this.$emit('onStopDraggingPanorama', mouseEvent)
            this.triggerStopViewerMovement()
        },

        removeListenerViewerCoordinatesTouch() {
            this.triggerStopViewerMovement()
        },

        mouseLeftViewer(mouseEvent) {
            this.triggerStopViewerMovement()
        },

        triggerStopViewerMovement() {
            var evt = document.createEvent('MouseEvents')
            evt.initEvent('mouseup', true, true)
            document.dispatchEvent(evt)
            let element = document.getElementById(this.id)
            if (element != null) element.removeEventListener('mousemove', this.onCurrentCoordinatesChange)
        },

        onDrop(ev) {
            this.$emit('drop', ev)
        },

        onDragOver(ev) {
            this.$emit('dragover', ev)
        },
    },
    mounted() {
        this.setupPanorama(false)
    },
    beforeDestroy() {
        this.destroy()
    },
}
</script>
<style lang="scss" scoped>
.panorama-container {
    width: 100%;
    height: 100%;
}
</style>
<style lang="scss">
@import '@/scss/components/360Viewer/UI/panorama-viewer.scss';
</style>
